import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ModalClienteMapa from "./modal-cliente-mapa";
import ModalClientesPorGeocercaMapa from "./modal-clientes-por-geocerca-mapa";

/**
 * @param {object} coord
 * @returns {{ lat: number, lng: number } | null}
 */
function parseCoord(coord) {
    if (typeof coord !== "object" || coord == null) return null;
    const lat = parseFloat(coord.x ?? coord.lat ?? coord.latitud);
    const lng = parseFloat(coord.y ?? coord.lng ?? coord.longitud);
    if (Number.isNaN(lat) || Number.isNaN(lng) || !isFinite(lat) || !isFinite(lng)) {
        return null;
    }
    return { lat, lng };
}

/** Remove consecutive duplicate vertices (common in API payloads). */
function dedupeAdjacentPath(path) {
    if (!path || path.length === 0) return path;
    const out = [path[0]];
    for (let i = 1; i < path.length; i += 1) {
        const prev = out[out.length - 1];
        const cur = path[i];
        if (prev.lat !== cur.lat || prev.lng !== cur.lng) {
            out.push(cur);
        }
    }
    return out;
}

/**
 * "lat,lng;lat,lng;..." (RP502 / seguimiento report style).
 * @param {string} s
 * @returns {Array<{lat:number,lng:number}>}
 */
function pathFromLatLngSemicolonString(s) {
    if (typeof s !== "string" || !s.trim()) return [];
    const segments = s.split(";").map((p) => p.trim()).filter(Boolean);
    const path = [];
    for (const seg of segments) {
        const idx = seg.indexOf(",");
        if (idx === -1) continue;
        const lat = parseFloat(seg.slice(0, idx));
        const lng = parseFloat(seg.slice(idx + 1));
        if (!Number.isNaN(lat) && !Number.isNaN(lng) && isFinite(lat) && isFinite(lng)) {
            path.push({ lat, lng });
        }
    }
    return dedupeAdjacentPath(path);
}

/**
 * Build closed rings from item.coordenadas: [[{x,y},...]] or flat [{x,y},...].
 * Also supports coordenadasGeocerca / coordenasGeocerca (API typo) as semicolon-separated lat,lng strings.
 * @param {object} item
 * @returns {Array<Array<{lat:number,lng:number}>>}
 */
export function ringsFromGeocercaItem(item) {
    let coords =
        item?.coordenadas ??
        item?.coordenadasGeocerca ??
        item?.coordenasGeocerca;
    if (coords == null) return [];

    if (typeof coords === "string") {
        const trimmed = coords.trim();
        if (!trimmed) return [];
        try {
            coords = JSON.parse(trimmed);
        } catch {
            const path = pathFromLatLngSemicolonString(trimmed);
            if (path.length >= 3) {
                return [path];
            }
            return [];
        }
    }
    if (!Array.isArray(coords) || coords.length === 0) return [];

    const rings = [];

    if (Array.isArray(coords[0])) {
        coords.forEach((ring) => {
            if (!Array.isArray(ring)) return;
            const path = dedupeAdjacentPath(
                ring.map(parseCoord).filter(Boolean)
            );
            if (path.length >= 3) {
                rings.push(path);
            }
        });
    } else {
        const path = dedupeAdjacentPath(
            coords.map(parseCoord).filter(Boolean)
        );
        if (path.length >= 3) {
            rings.push(path);
        }
    }

    return rings;
}

const DEFAULT_CENTER = { lat: -17.78, lng: -63.18 };
const MAPS_POLL_MS = 150;
const MAPS_POLL_MAX_MS = 8000;

/** Google Maps pin path (same as mapa-ruteo). */
const PIN_PATH =
    "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z";

let LabelBelowMarkerOverlayClass = null;

function ensureLabelBelowMarkerOverlayClass() {
    if (LabelBelowMarkerOverlayClass || !window.google?.maps) {
        return LabelBelowMarkerOverlayClass;
    }
    const maps = window.google.maps;

    function LabelBelowMarkerOverlay(position, text, map) {
        this.position = position;
        this.text = text;
        this.setMap(map);
    }

    LabelBelowMarkerOverlay.prototype = new maps.OverlayView();

    LabelBelowMarkerOverlay.prototype.onAdd = function () {
        const div = document.createElement("div");
        div.style.cssText = [
            "position:absolute",
            "max-width:220px",
            "white-space:normal",
            "text-align:center",
            "font-size:11px",
            "line-height:1.3",
            "font-weight:600",
            "color:#111827",
            "background:rgba(255,255,255,0.95)",
            "padding:4px 8px",
            "border-radius:6px",
            "border:1px solid rgba(0,0,0,0.12)",
            "box-shadow:0 1px 3px rgba(0,0,0,0.12)",
            "pointer-events:none",
            "transform:translate(-50%, 0)",
        ].join(";");
        div.textContent = this.text || "";
        this.div = div;
        const panes = this.getPanes();
        if (panes?.overlayMouseTarget) {
            panes.overlayMouseTarget.appendChild(div);
        }
    };

    LabelBelowMarkerOverlay.prototype.draw = function () {
        if (!this.div || !this.position) return;
        const projection = this.getProjection();
        if (!projection) return;
        const point = projection.fromLatLngToDivPixel(this.position);
        if (!point) return;
        const offsetBelowPinPx = 14;
        this.div.style.left = `${point.x}px`;
        this.div.style.top = `${point.y + offsetBelowPinPx}px`;
    };

    LabelBelowMarkerOverlay.prototype.onRemove = function () {
        if (this.div?.parentNode) {
            this.div.parentNode.removeChild(this.div);
        }
        this.div = null;
    };

    LabelBelowMarkerOverlayClass = LabelBelowMarkerOverlay;
    return LabelBelowMarkerOverlayClass;
}

/**
 * Clears and optionally rebuilds client description overlays (no fitBounds / zoom change).
 */
function syncClientLabelOverlays(map, clients, showLabels, labelOverlaysRef) {
    labelOverlaysRef.current.forEach((o) => o.setMap(null));
    labelOverlaysRef.current = [];
    if (!showLabels || !map || !window.google?.maps) {
        return;
    }
    const LabelOverlay = ensureLabelBelowMarkerOverlayClass();
    if (!LabelOverlay) {
        return;
    }
    const list = Array.isArray(clients) ? clients : [];
    list.forEach((cliente) => {
        const lat = parseFloat(cliente.latitud);
        const lng = parseFloat(cliente.longitud);
        if (Number.isNaN(lat) || Number.isNaN(lng) || !isFinite(lat) || !isFinite(lng)) {
            return;
        }
        if (cliente.descripcion == null || String(cliente.descripcion).trim() === "") {
            return;
        }
        const position = new window.google.maps.LatLng(lat, lng);
        const text = String(cliente.descripcion).trim();
        const overlay = new LabelOverlay(position, text, map);
        labelOverlaysRef.current.push(overlay);
    });
}

/** Clients whose idGeocerca matches geofence item.value (string-safe). */
export function filterClientesPorGeocerca(item, listaClientes) {
    const arr = Array.isArray(listaClientes) ? listaClientes : [];
    if (item == null || item.value == null) {
        return [];
    }
    const target = String(item.value);
    return arr.filter(
        (c) => c != null && c.idGeocerca != null && String(c.idGeocerca) === target
    );
}

/**
 * Opens the geofence clients modal: filters listaClientes and sets modal payload.
 * @param {function} setModal - setState receiving { item, clientes }
 * @returns {Array} filtered clientes
 */
export function defaultOnGeocercaClick(item, listaClientes, setModal) {
    const clientes = filterClientesPorGeocerca(item, listaClientes);
    if (typeof setModal === "function") {
        setModal({ item, clientes });
    }
    return clientes;
}

/**
 * Google Map showing geofence polygons from listaGeocercas-style items:
 * { value, label, coordenadas, colorGeocerca } (label may be nombreGeocerca elsewhere).
 *
 * @param {Array<object>} [listaClientes] - Clients with latitud, longitud, color, descripcion (marker + optional label below).
 * @param {boolean} [mostrarEtiquetasMarcadores] - When true, show `descripcion` under each client marker.
 * @param {function(object, object[], object[]): void} [onGeocercaClick] - After the modal opens: (geocercaItem, listaClientes, clientesFiltrados).
 */
export default function MapaGeocercasCliente({
    geocercas = [],
    listaClientes = [],
    mostrarEtiquetasMarcadores = true,
    onGeocercaClick,
    className = "",
    minHeightClass = "h-[min(70vh,640px)] min-h-[360px]",
}) {
    const { t } = useTranslation();
    const containerRef = useRef(null);
    const mapRef = useRef(null);
    const polygonsRef = useRef([]);
    const markersRef = useRef([]);
    const labelOverlaysRef = useRef([]);
    const listaClientesRef = useRef(listaClientes);
    const mostrarEtiquetasRef = useRef(mostrarEtiquetasMarcadores);
    const onGeocercaClickRef = useRef(onGeocercaClick);
    const [mapsError, setMapsError] = useState(false);
    const [clienteModal, setClienteModal] = useState(null);
    const [geocercaClientesModal, setGeocercaClientesModal] = useState(null);

    listaClientesRef.current = listaClientes;
    mostrarEtiquetasRef.current = mostrarEtiquetasMarcadores;
    onGeocercaClickRef.current = onGeocercaClick;

    useEffect(() => {
        let cancelled = false;
        let pollTimer = null;
        const start = Date.now();

        const draw = () => {
            if (cancelled || !containerRef.current) return;
            if (!window.google?.maps) {
                if (Date.now() - start > MAPS_POLL_MAX_MS) {
                    setMapsError(true);
                    return;
                }
                pollTimer = window.setTimeout(draw, MAPS_POLL_MS);
                return;
            }

            setMapsError(false);

            if (!mapRef.current) {
                mapRef.current = new window.google.maps.Map(containerRef.current, {
                    center: DEFAULT_CENTER,
                    zoom: 12,
                    mapTypeControl: true,
                    streetViewControl: false,
                    fullscreenControl: true,
                    zoomControl: true,
                });
            }

            const map = mapRef.current;
            polygonsRef.current.forEach((p) => p.setMap(null));
            polygonsRef.current = [];
            markersRef.current.forEach((m) => m.setMap(null));
            markersRef.current = [];
            labelOverlaysRef.current.forEach((o) => o.setMap(null));
            labelOverlaysRef.current = [];

            const list = Array.isArray(geocercas) ? geocercas : [];
            const bounds = new window.google.maps.LatLngBounds();
            let hasPoint = false;

            list.forEach((item) => {
                const color = item.colorGeocerca || "#3388ff";
                const rings = ringsFromGeocercaItem(item);

                rings.forEach((path) => {
                    path.forEach((pt) => {
                        bounds.extend(pt);
                        hasPoint = true;
                    });
                    const polygon = new window.google.maps.Polygon({
                        paths: path,
                        strokeColor: color,
                        strokeOpacity: 0.85,
                        strokeWeight: 2,
                        fillColor: color,
                        fillOpacity: 0.28,
                        map,
                    });
                    polygon.addListener("click", () => {
                        const list = listaClientesRef.current;
                        const clientes = defaultOnGeocercaClick(item, list, setGeocercaClientesModal);
                        const extra = onGeocercaClickRef.current;
                        if (typeof extra === "function") {
                            extra(item, list, clientes);
                        }
                    });
                    polygonsRef.current.push(polygon);
                });
            });

            const clients = Array.isArray(listaClientes) ? listaClientes : [];

            clients.forEach((cliente) => {
                const lat = parseFloat(cliente.latitud);
                const lng = parseFloat(cliente.longitud);
                if (Number.isNaN(lat) || Number.isNaN(lng) || !isFinite(lat) || !isFinite(lng)) {
                    return;
                }
                const color = cliente.color && String(cliente.color).trim() !== ""
                    ? cliente.color
                    : "#6b7280";
                const position = new window.google.maps.LatLng(lat, lng);
                bounds.extend(position);
                hasPoint = true;

                const icon = {
                    path: PIN_PATH,
                    fillColor: color,
                    fillOpacity: 1,
                    strokeColor: "#ffffff",
                    strokeWeight: 2,
                    scale: 2,
                    anchor: new window.google.maps.Point(12, 22),
                    labelOrigin: new window.google.maps.Point(12, 9.5),
                };

                const marker = new window.google.maps.Marker({
                    map,
                    position: { lat, lng },
                    title: cliente.descripcion || cliente.codigo || "",
                    icon,
                });
                marker.addListener("click", () => {
                    setClienteModal(cliente);
                });
                markersRef.current.push(marker);
            });

            if (hasPoint && !bounds.isEmpty()) {
                map.fitBounds(bounds, 56);
            }

            syncClientLabelOverlays(
                map,
                listaClientesRef.current,
                mostrarEtiquetasRef.current,
                labelOverlaysRef
            );
        };

        draw();

        return () => {
            cancelled = true;
            if (pollTimer) window.clearTimeout(pollTimer);
        };
    }, [geocercas, listaClientes]);

    useEffect(() => {
        if (!mapRef.current || !window.google?.maps) {
            return;
        }
        syncClientLabelOverlays(
            mapRef.current,
            listaClientesRef.current,
            mostrarEtiquetasMarcadores,
            labelOverlaysRef
        );
    }, [mostrarEtiquetasMarcadores]);

    useEffect(() => {
        return () => {
            polygonsRef.current.forEach((p) => p.setMap(null));
            markersRef.current.forEach((m) => m.setMap(null));
            labelOverlaysRef.current.forEach((o) => o.setMap(null));
            polygonsRef.current = [];
            markersRef.current = [];
            labelOverlaysRef.current = [];
            mapRef.current = null;
        };
    }, []);

    return (
        <div className={`relative w-full ${minHeightClass} ${className}`}>
            <div
                ref={containerRef}
                className="h-full w-full rounded-lg border border-gray-200 bg-gray-100"
            />
            {mapsError && (
                <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/90 p-4 text-center text-sm text-gray-600">
                    {t("label_error_carga_mapa")}
                </div>
            )}

            <ModalClienteMapa
                isOpen={clienteModal != null}
                onClose={() => setClienteModal(null)}
                cliente={clienteModal}
            />

            <ModalClientesPorGeocercaMapa
                isOpen={geocercaClientesModal != null}
                onClose={() => setGeocercaClientesModal(null)}
                geocercaItem={geocercaClientesModal?.item}
                clientes={geocercaClientesModal?.clientes ?? []}
            />
        </div>
    );
}
